import React, { useEffect, useState, useRef } from "react";
import { axiosClientSignIn } from "../../axios/api";
import { getSocket } from "../../socket/socket.js";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../Loader/Loader.jsx";
import TypingIndicator from './TypingIndicator.jsx';
import { selectThreadTyping, fetchThreadConversation, selectThreadConversation } from '../../slice/threadSlice.jsx';
import { Box, Typography, Chip, Stack, Divider, Button } from '@mui/material';
const ThreadWindow = ({ threadId, threadDataId, participants = [] }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [threadData, setThreadData] = useState({
    threadId: null,
    mainMessage: null,
    messages: [],
  });
  
  const [threadInput, setThreadInput] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [otherUsersTyping, setOtherUsersTyping] = useState([]);
  const typingTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Get the effective thread ID (canonical is Thread._id)
  // Priority: threadData.threadId (from API) -> threadDataId (prop: Thread._id) -> threadId (prop: main message id)
  const currentThreadId = (threadData && threadData.threadId) || threadDataId || threadId;
  
  // Get typing indicators from Redux as fallback/supplement to local state
  const reduxTyping = useSelector(selectThreadTyping(currentThreadId)) || {};
  
  console.log(threadData, 'threaddata--------------->');
  
  // Initialize socket once
  useEffect(() => {
    const socketInstance = getSocket();
    setSocket(socketInstance);
    
    return () => {
      // Cleanup on unmount
      if (socketInstance) {
        socketInstance.off('connect');
        socketInstance.off('disconnect');
        socketInstance.off('receiveThreadMessage');
        socketInstance.off('threadTyping');
        socketInstance.off('threadTypingStop');
      }
      
      // Stop typing and clear timeout on unmount
      if (isTyping) {
        handleTypingStop();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (threadId) {
      dispatch(fetchThreadConversation(threadId));
    }
  }, [threadId, dispatch]);

  // Get thread conversation from Redux
  const reduxThreadData = useSelector(selectThreadConversation(threadId));
  
  // Use Redux data if available, otherwise use local state as fallback
  const effectiveThreadData = reduxThreadData || threadData;
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (effectiveThreadData.messages?.length > 0 && messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }, 100);
    }
  }, [effectiveThreadData.messages]);
  
  // Typing indicator functions
  const handleTypingStart = () => {
    if (!currentThreadId || isTyping) return;
    
    setIsTyping(true);
    const socket = getSocket();
    
    if (!socket || !socket.connected) {
      return;
    }
    
    const typingData = {
      threadId: currentThreadId,
      userId: user.id,
      senderName: user.firstName || user.userName || 'Someone'
    };
    
    console.log('📤 Emitting startThreadTyping:', typingData);
    socket.emit('startThreadTyping', typingData);
    
    // Auto stop typing after 3 seconds of inactivity
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      console.log('⏰ Auto-stopping thread typing after 3 seconds');
      handleTypingStop();
    }, 3000);
  };
  
  const handleTypingStop = () => {
    if (!currentThreadId || !isTyping) return;
    
    setIsTyping(false);
    const socket = getSocket();
    
    if (!socket || !socket.connected) {
      return;
    }
    
    const typingData = {
      threadId: currentThreadId,
      userId: user.id,
      senderName: user.firstName || user.userName || 'Someone'
    };
    
    console.log('📤 Emitting stopThreadTyping:', typingData);
    socket.emit('stopThreadTyping', typingData);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };


  // Handle socket connection status
  useEffect(() => {
    if (!socket) return;
    
    // Set initial connection status
    setIsConnected(socket.connected);
    
    // Handle connection events
    const handleConnect = () => {
      console.log('Thread socket connected');
      setIsConnected(true);
    };
    
    const handleDisconnect = () => {
      console.log('Thread socket disconnected');
      setIsConnected(false);
    };
    
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);
  
  
  // Handle receiving thread messages and typing indicators
  useEffect(() => {
    if (!socket) return;
    
    const handleReceiveMessage = async (messageData) => {
      console.log('📨 Received thread message:', messageData);
      console.log('🔍 Debug - Current threadData.threadId:', threadData.threadId);
      console.log('🔍 Debug - Message conversation:', messageData.conversation);
      console.log('🔍 Debug - URL threadId:', threadId);
       dispatch( await fetchThreadConversation(threadId));
 
    };
    
    const handleThreadTyping = (data) => {
      console.log('👀 Thread typing START event received:', data);
      
      // Check if this typing event is for the current thread and not from current user
      const isForCurrentThread = data.threadId === currentThreadId;
      const isFromOtherUser = data.userId !== user.id;
      
      console.log('🔍 Debug - Typing data.threadId:', data.threadId);
      console.log('🔍 Debug - Current threadData.threadId:', threadData.threadId);
      console.log('🔍 Debug - URL threadId:', threadId);
      console.log('🔍 Debug - isForCurrentThread:', isForCurrentThread);
      console.log('🔍 Debug - isFromOtherUser:', isFromOtherUser);
      
      if (isForCurrentThread && isFromOtherUser) {
        console.log('✅ Adding thread typing indicator');
        
        // Add/update typing user
        setOtherUsersTyping(prev => {
          const filtered = prev.filter(u => u.userId !== data.userId);
          return [...filtered, {
            userId: data.userId,
            senderName: data.senderName,
            timestamp: Date.now()
          }];
        });
      } else {
        console.log('❌ Ignoring typing start event (not for this thread or from current user)');
      }
    };
    
    const handleThreadTypingStop = (data) => {
      console.log('👀 Thread typing STOP event received:', data);
      
      // Check if this typing event is for the current thread and not from current user
      const isForCurrentThread = data.threadId === currentThreadId;
      const isFromOtherUser = data.userId !== user.id;
      
      if (isForCurrentThread && isFromOtherUser) {
        console.log('✅ Removing thread typing indicator');
        
        // Remove typing user
        setOtherUsersTyping(prev => 
          prev.filter(u => u.userId !== data.userId)
        );
      } else {
        console.log('❌ Ignoring typing stop event (not for this thread or from current user)');
      }
    };

    socket.on('receiveThreadMessage', handleReceiveMessage);
    socket.on('threadTyping', handleThreadTyping);
    socket.on('threadTypingStop', handleThreadTypingStop);
    
    return () => {
      socket.off('receiveThreadMessage', handleReceiveMessage);
      socket.off('threadTyping', handleThreadTyping);
      socket.off('threadTypingStop', handleThreadTypingStop);
    };
  }, [socket, threadId, user.id]); // Removed threadData.threadId to prevent too many re-renders

  const handleSendThreadMessage = async () => {
    // Stop typing when sending message
    if (isTyping) {
      handleTypingStop();
    }
    
    if (!socket || !socket.connected) {
      console.error('Socket not connected');
      return;
    }
    
    if (!threadInput.trim()) {
      console.warn('Cannot send empty message');
      return;
    }
         dispatch(fetchThreadConversation(threadId));
    console.log('Sending thread message via socket:', socket);
    
    const newMessage = {
      sender: user.id,
      threadId: effectiveThreadData.threadId,
      content: threadInput.trim(),
    };
    
    socket.emit('sendThreadMessage', newMessage);
    setThreadInput('');
    
    // Auto-scroll after sending (small delay to ensure message appears)
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 200);
    
    // Real-time update will come via receiveThreadMessage socket event
  };

  if (!isConnected) {
    return <Loading />
  }

  return (
    <div>
      {/* {threadData.mainMessage.threads[0].allowed.map((item) => {
  return <p key={item._id}>{item.firstname}</p>;
})} */}
         
        <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 0 }}>
        {effectiveThreadData.mainMessage && (
          <Box sx={{
            p: 2.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
            border: '1px solid',
            borderColor: 'divider',
            mb: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {effectiveThreadData.mainMessage.sender.firstName?.[0]?.toUpperCase() || '?'}
              </Box>
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {effectiveThreadData.mainMessage.sender.firstName}
                  </Typography>
                  <Chip 
                    label="Original"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
                </Stack>
                
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  lineHeight: 1.5
                }}>
                  {effectiveThreadData.mainMessage.content}
                </Typography>
                
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {new Date(effectiveThreadData.mainMessage.createdAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            💭 Replies
          </Typography>
          <Chip 
            label={`${(effectiveThreadData.messages || []).length}`}
            size="small"
            color="primary"
            variant="filled"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Stack>
        
        {/* Thread Status Bar */}
        <Box sx={{
          mb: 2,
          p: 2,
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              💬 Thread Discussion
            </Typography>
            {isTyping && (
              <Chip
                label="You're typing"
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Stack>

          {/* Participants */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Participants:
            </Typography>
            {(() => {
              const names = Array.from(new Set(
                (participants || []).map((allowed) => {
                  const u = allowed?.user;
                  if (u && typeof u === 'object') return u.firstName || u.name || 'Unknown';
                  const idStr = (allowed?.user && allowed.user.toString && allowed.user.toString()) || '';
                  return `User ${idStr.slice(-4) || 'Unknown'}`;
                })
              ));
              return names.length ? names.map((n) => (
                <Chip
                  key={n}
                  label={n}
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: 'primary.50', borderColor: 'primary.200' }}
                />
              )) : <Typography variant="caption" sx={{ color: 'text.disabled' }}>No participants</Typography>;
            })()}
          </Stack>

          {/* Live Typing Indicators */}
          {(() => {
            const localTypingUsers = otherUsersTyping;
            const reduxTypingUsers = Object.values(reduxTyping).filter(user => user.isTyping && user.senderName);
            const names = Array.from(new Set([
              ...localTypingUsers.map(u => u.senderName),
              ...reduxTypingUsers.map(u => u.senderName)
            ]));
            
            if (names.length === 0) return null;
            
            return (
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  ✍️ Currently typing:
                </Typography>
                {names.map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                        '100%': { opacity: 1 }
                      }
                    }}
                  />
                ))}
              </Stack>
            );
          })()}
        </Box>
        
        {/* Thread Info Bar (useful UI) */}
        <Box sx={{
          mb: 1.5,
          p: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}>
         

          {/* Participants */}
          
        </Box>
        <Box 
          ref={messagesContainerRef}
          sx={{ 
            maxHeight: 400,
            overflowY: 'auto',
            mb: 2,
            bgcolor: '#fafafa',
            borderRadius: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            '&::-webkit-scrollbar': {
              width: 4
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0,0,0,0.2)',
              borderRadius: 2
            }
          }}
        >
          {(effectiveThreadData.messages || []).length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.disabled'
            }}>
              <Typography variant="body2">
                No replies yet. Start the discussion! 💬
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {(effectiveThreadData.messages || []).map((msg, index) => (
                <Box 
                  key={msg._id}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <Stack direction="row" spacing={1.5}>
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {msg.sender.firstName?.[0]?.toUpperCase() || '?'}
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {msg.sender.firstName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          #{index + 1}
                        </Typography>
                      </Stack>
                      
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        lineHeight: 1.4,
                        wordBreak: 'break-word'
                      }}>
                        {msg.content}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
        
        {/* Reply Input Section */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-end">
            <Box sx={{ flex: 1 }}>
              <Box
                component="textarea"
                value={threadInput}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setThreadInput(newValue);
                  
                  console.log('🏠 Input changed. Value:', JSON.stringify(newValue), 'Trimmed length:', newValue.trim().length, 'Currently typing:', isTyping);
                  
                  // Start typing indicator when user starts typing
                  if (newValue.trim() && !isTyping) {
                    console.log('🟢 Should start typing indicator');
                    handleTypingStart();
                  } else if (!newValue.trim() && isTyping) {
                    console.log('🔴 Should stop typing indicator');
                    handleTypingStop();
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendThreadMessage();
                  }
                }}
                placeholder="Type your reply..."
                disabled={!isConnected}
                sx={{
                  width: '100%',
                  minHeight: 60,
                  maxHeight: 120,
                  p: 1.5,
                  border: '2px solid',
                  borderColor: threadInput.trim() ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  outline: 'none',
                  bgcolor: 'white',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  transition: 'border-color 0.2s ease',
                  '&::placeholder': {
                    color: 'text.disabled'
                  },
                  '&:disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'text.disabled'
                  }
                }}
              />
            </Box>
            
            <Button
              variant="contained"
              onClick={handleSendThreadMessage}
              disabled={!isConnected || !threadInput.trim()}
              sx={{
                minWidth: 80,
                height: 40,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'none'
              }}
            >
              {!isConnected ? 'Offline' : 'Reply'}
            </Button>
          </Stack>
          
          {threadInput.trim() && (
            <Typography variant="caption" sx={{ 
              color: 'text.disabled',
              mt: 0.5,
              display: 'block',
              textAlign: 'right'
            }}>
              Press Enter to send • {threadInput.length}/500
            </Typography>
          )}
        </Box>
        </Box>
      </div>
  );
};

export default ThreadWindow;
